import React from 'react';
import { Typography, Grid, TextField, Button, IconButton, Paper, Chip, Box, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import SpeciesPicker from './SpeciesPicker';

const StandDetailsPanel = ({ 
    selectedStand,
    standDetails, 
    setStandDetails, 
    standSpecies,
    setStandSpecies,
    handleUpdateStand, 
    handleDeleteStand, 
    activities, 
    handleActivityClick, 
    handleViewAllForStand 
}) => {
    if (!selectedStand) return null;

    const standActivities = activities
        .filter(a => a.stand_id === selectedStand.id)
        .sort((a, b) => {
            if (a.planned_year !== b.planned_year) return a.planned_year - b.planned_year;
            return a.planned_quarter - b.planned_quarter;
        })
        .slice(0, 5);

    return (
        <div style={{ marginTop: '20px', padding: '15px', border: '1px solid #ccc', borderRadius: '4px' }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="h6">Stand Details (Stand {selectedStand.properties.stand_number})</Typography>
                <IconButton color="error" onClick={handleDeleteStand} size="small">
                    <DeleteIcon />
                </IconButton>
            </Box>
            
            <Grid container spacing={2}>
                <Grid item xs={6}>
                    <TextField
                        label="Age"
                        type="number"
                        fullWidth
                        size="small"
                        value={standDetails.age}
                        onChange={(e) => setStandDetails({ ...standDetails, age: parseInt(e.target.value) })}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Area (ha)"
                        type="number"
                        fullWidth
                        size="small"
                        value={selectedStand.properties.area_hectares || 0}
                        InputProps={{
                            readOnly: true,
                        }}
                        variant="filled"
                        helperText="Calculated from map"
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Site Index"
                        fullWidth
                        size="small"
                        value={standDetails.site_index}
                        onChange={(e) => setStandDetails({ ...standDetails, site_index: e.target.value })}
                    />
                </Grid>
                <Grid item xs={6}>
                    <TextField
                        label="Volume (m3sk/ha)"
                        type="number"
                        fullWidth
                        size="small"
                        value={standDetails.volume_m3sk_ha}
                        onChange={(e) => setStandDetails({ ...standDetails, volume_m3sk_ha: parseInt(e.target.value) })}
                    />
                </Grid>
                <Grid item xs={6}>
                    <FormControl fullWidth size="small">
                        <InputLabel>Goal Class</InputLabel>
                        <Select
                            value={['PG', 'K', 'NS', 'NO'].includes(standDetails.goal_class) ? standDetails.goal_class : 'PG'}
                            label="Goal Class"
                            onChange={(e) => setStandDetails({ ...standDetails, goal_class: e.target.value })}
                        >
                            <MenuItem value="PG">Production (PG)</MenuItem>
                            <MenuItem value="K">Nature (K)</MenuItem>
                            <MenuItem value="NS">Nature Managed (NS)</MenuItem>
                            <MenuItem value="NO">Nature Untouched (NO)</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={12}>
                    <TextField
                        label="Density (stems/ha)"
                        type="number"
                        fullWidth
                        size="small"
                        value={standDetails.stems_per_ha}
                        onChange={(e) => setStandDetails({ ...standDetails, stems_per_ha: parseInt(e.target.value) })}
                    />
                </Grid>
                <Grid item xs={12}>
                    <SpeciesPicker standSpecies={standSpecies} setStandSpecies={setStandSpecies} />
                </Grid>
                <Grid item xs={12}>
                    <Button variant="contained" fullWidth onClick={handleUpdateStand}>
                        Update Stand Info
                    </Button>
                </Grid>
            </Grid>

            <div style={{ marginTop: '20px' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Typography variant="subtitle1" fontWeight="bold">Upcoming Activities</Typography>
                    <Button size="small" onClick={() => handleViewAllForStand(selectedStand.id)}>View All</Button>
                </Box>
                {standActivities.length > 0 ? (
                    standActivities.map(a => (
                        <Paper 
                            key={a.id} 
                            elevation={1} 
                            sx={{ 
                                padding: '4px', 
                                marginBottom: '4px', 
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: '#f0f4f8' }
                            }}
                            onClick={() => handleActivityClick(a)}
                        >
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                <Typography variant="body2">{a.activity_type}</Typography>
                                <Typography variant="caption" color="textSecondary">{a.planned_year} Q{a.planned_quarter}</Typography>
                            </Box>
                        </Paper>
                    ))
                ) : (
                    <Typography variant="body2" color="textSecondary" sx={{ fontStyle: 'italic' }}>
                        No activities planned
                    </Typography>
                )}
            </div>
        </div>
    );
};

export default StandDetailsPanel;
